import { createApi } from "@reduxjs/toolkit/query/react";

import { getBaseQuery } from "../baseQuery";

import type { AccountFile } from "../../../types/AccountFile";
import { parseSpreadsheet, type SheetData } from "./helpers";

export const accountFilesService = createApi({
  reducerPath: "accountFilesService",
  baseQuery: getBaseQuery("zeus"),
  tagTypes: ["AccountFiles"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getAccountFiles: builder.query<
      AccountFile[],
      { scope?: AccountFile["scope"] } | void
    >({
      query: (params) => ({
        url: "/api/files",
        ...(params && { params }),
      }),
      providesTags: [{ type: "AccountFiles", id: "LIST" }],
    }),

    uploadAccountFile: builder.mutation<AccountFile, FormData>({
      query: (formData) => ({
        url: "/api/files",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "AccountFiles", id: "LIST" }],
    }),

    deleteAccountFile: builder.mutation<void, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/api/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AccountFiles", id: "LIST" }],
    }),

    getFileRetrieveUrl: builder.query<{ url: string }, { fileId: string }>({
      query: ({ fileId }) => `/api/files/${fileId}/retrieve`,
      keepUnusedDataFor: 600,
    }),

    getSpreadsheetData: builder.query<SheetData[], { fileId: string; filename?: string }>(
      {
        queryFn: async ({ fileId, filename }, queryApi) => {
          // Reuse the getFileRetrieveUrl endpoint so both FileViewerPanel and getSpreadsheetData
          // share the same RTK Query cache entry, avoiding a duplicate /retrieve request.
          const urlResult = await queryApi.dispatch(
            accountFilesService.endpoints.getFileRetrieveUrl.initiate({
              fileId,
            }),
          );
          if (urlResult.error) {
            return { error: urlResult.error };
          }

          try {
            const response = await fetch(urlResult.data.url);
            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data: "Failed to download file",
                },
              };
            }

            const arrayBuffer = await response.arrayBuffer();
            return { data: parseSpreadsheet(arrayBuffer, filename) };
          } catch (e) {
            return {
              error: {
                status: "FETCH_ERROR" as const,
                error: e instanceof Error ? e.message : "Failed to load file",
              },
            };
          }
        },
        keepUnusedDataFor: 300,
      },
    ),
  }),
});

export const {
  useGetAccountFilesQuery,
  useUploadAccountFileMutation,
  useDeleteAccountFileMutation,
  useGetFileRetrieveUrlQuery,
  useLazyGetFileRetrieveUrlQuery,
  useGetSpreadsheetDataQuery,
} = accountFilesService;
